import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Volume2, VolumeX, X, Play, Loader2, ArrowLeft } from "lucide-react";
import { getIconicList } from "@/lib/iconic-dates";
import { pickAmbientTrack } from "@/lib/ambient-tracks";
import { postTimeJump, fetchHistoricalMedia } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

/**
 * TVAmbient — full-screen looping "attract mode" montage for smart TVs.
 *
 * • Picks 5 iconic dates from the user's region (default US)
 * • Each date shows for ~30s with a slow Ken-Burns photo
 * • Muted by default (screensaver-friendly); tap unmute icon to enable audio
 * • Tap anywhere else → jumps into full Cinema Mode for that date
 * • Auto-loops forever
 *
 * URL params
 * ----------
 * ?region=GB  — override region (defaults to localStorage `tc_region` or "US")
 */

const SHOW_MS = 30_000;          // each iconic date on screen for 30s
const IMAGE_SWAP_MS = 8_000;      // within a segment, rotate photos every 8s
const MAX_ENTRIES = 5;

export default function TVAmbient() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const region = (searchParams.get("region") || "").toUpperCase() ||
    (() => { try { return localStorage.getItem("tc_region") || "US"; } catch { return "US"; } })();

  // Segments = 5 iconic dates. Each is enriched with snapshot_id + images as they load.
  const [segments, setSegments] = useState(() =>
    getIconicList(region).slice(0, MAX_ENTRIES).map((e) => ({
      ...e,
      snapshotId: null,
      images: [],
      loaded: false,
    })),
  );
  const [activeIdx, setActiveIdx] = useState(0);
  const [imgIdx, setImgIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [chromeVisible, setChromeVisible] = useState(true);
  const audioRef = useRef(null);
  const segTimerRef = useRef(null);
  const imgTimerRef = useRef(null);
  const chromeTimerRef = useRef(null);

  // Load snapshots + media for all segments (parallelized)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const enriched = await Promise.all(
        segments.map(async (seg) => {
          try {
            const snap = await postTimeJump({ date: seg.date, region: seg.region });
            const media = await fetchHistoricalMedia(snap.id);
            const images = (media?.images || [])
              .map((im) => im.thumb_url || im.full_url)
              .filter(Boolean)
              .slice(0, 3);
            return { ...seg, snapshotId: snap.id, images, loaded: true };
          } catch {
            return { ...seg, loaded: true };
          }
        }),
      );
      if (!cancelled) setSegments(enriched);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  // Segment auto-advance every 30s
  useEffect(() => {
    segTimerRef.current = setInterval(() => {
      setActiveIdx((i) => (i + 1) % MAX_ENTRIES);
      setImgIdx(0);
    }, SHOW_MS);
    return () => clearInterval(segTimerRef.current);
  }, []);

  // Within a segment, cycle photos every 8s. Reset imgIdx when segment
  // changes so it never grows unbounded during multi-day attract-mode runs.
  useEffect(() => {
    imgTimerRef.current = setInterval(() => {
      setImgIdx((i) => (i + 1) % 1000);  // hard cap to prevent unbounded growth
    }, IMAGE_SWAP_MS);
    return () => clearInterval(imgTimerRef.current);
  }, []);

  // Ambient audio — track chosen based on the active segment's era
  const active = segments[activeIdx] || null;
  const activeYear = active ? parseInt((active.date || "1970").slice(0, 4), 10) : 1970;
  const ambientTrack = useMemo(() => pickAmbientTrack(activeYear), [activeYear]);

  // Restart audio when track changes
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.src = ambientTrack.file;
    audioRef.current.volume = muted ? 0 : 0.4;
    audioRef.current.loop = true;
    if (!muted) {
      audioRef.current.play().catch(() => {});
    }
  }, [ambientTrack.file, muted]);

  // Auto-hide the chrome after 4s of no interaction
  const bumpChrome = () => {
    setChromeVisible(true);
    if (chromeTimerRef.current) clearTimeout(chromeTimerRef.current);
    chromeTimerRef.current = setTimeout(() => setChromeVisible(false), 4000);
  };
  useEffect(() => {
    bumpChrome();
    return () => chromeTimerRef.current && clearTimeout(chromeTimerRef.current);
  }, []);

  // Track when ambient mode starts
  useEffect(() => {
    trackEvent("tv_ambient_start", { region });
  }, [region]);

  const activeImgs = active?.images?.length ? active.images : [];
  const currentImg = activeImgs.length > 0 ? activeImgs[imgIdx % activeImgs.length] : null;
  const yearsAgo = (() => {
    if (!active) return null;
    const y = parseInt((active.date || "").slice(0, 4), 10);
    if (!y) return null;
    return new Date().getUTCFullYear() - y;
  })();

  const jumpToWatch = () => {
    if (!active?.snapshotId) return;
    trackEvent("tv_ambient_jump", { date: active.date, region: active.region });
    navigate(`/watch/${active.snapshotId}`);
  };

  return (
    <div
      className="fixed inset-0 bg-ink text-paper overflow-hidden"
      data-testid="tv-ambient"
      onMouseMove={bumpChrome}
      onTouchStart={bumpChrome}
      onKeyDown={bumpChrome}
    >
      {/* Background photo layers */}
      {activeImgs.length > 0 ? (
        activeImgs.map((src, i) => (
          <div
            key={`${active.date}-${src}`}
            className="absolute inset-0 transition-opacity duration-[1600ms] ease-in-out"
            style={{
              opacity: i === (imgIdx % activeImgs.length) ? 1 : 0,
              backgroundImage: `url(${src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "grayscale(20%) contrast(1.05) brightness(0.72)",
              transform: i === (imgIdx % activeImgs.length) ? "scale(1.12)" : "scale(1)",
              transformOrigin: i % 2 === 0 ? "20% 30%" : "80% 70%",
              transition: "opacity 1600ms ease-in-out, transform 30000ms ease-out",
            }}
          />
        ))
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 30% 40%, rgba(186,63,29,0.35), rgba(26,28,32,1) 60%), #1A1C20",
          }}
        />
      )}

      {/* Vignette + subtle grain */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 30%, rgba(26,28,32,0.85) 100%)",
        }}
      />

      {/* Full-canvas tap-to-watch layer (below chrome buttons) */}
      <button
        onClick={jumpToWatch}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        data-testid="tv-ambient-tap-layer"
        aria-label="Watch this day in Cinema Mode"
      />

      {/* Chrome — top bar */}
      <div
        className={`absolute top-0 inset-x-0 p-4 sm:p-6 flex items-center justify-between z-20 transition-opacity duration-500 ${
          chromeVisible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex items-center gap-3">
          <Link
            to="/tv"
            className="inline-flex items-center gap-1.5 bg-ink/60 backdrop-blur border-2 border-paper/40 text-paper px-3 py-2 font-mono uppercase tracking-widest text-[10px] hover:bg-paper hover:text-ink"
            data-testid="tv-ambient-exit"
            onClick={(e) => e.stopPropagation()}
          >
            <ArrowLeft size={12} /> Exit
          </Link>
          <div className="font-mono uppercase tracking-[0.3em] text-[10px] text-paper/70 hidden sm:block">
            TimeCapsule 24 · Ambient · {region}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }}
          className="inline-flex items-center gap-1.5 bg-ink/60 backdrop-blur border-2 border-paper/40 text-paper px-3 py-2 font-mono uppercase tracking-widest text-[10px] hover:bg-paper hover:text-ink"
          data-testid="tv-ambient-mute-toggle"
          title={muted ? "Unmute ambient music" : "Mute"}
        >
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          <span className="hidden sm:inline">{muted ? "Sound off" : "Sound on"}</span>
        </button>
      </div>

      {/* Chrome — centre caption + CTA */}
      <div
        className={`absolute inset-x-0 bottom-0 pb-10 sm:pb-16 px-6 sm:px-14 z-10 pointer-events-none transition-opacity duration-500 ${
          chromeVisible ? "opacity-100" : "opacity-40"
        }`}
      >
        {active ? (
          <div className="max-w-3xl">
            <div className="font-mono uppercase tracking-[0.35em] text-[10px] sm:text-xs text-paper/60 mb-2 flex items-center gap-2">
              <span>{active.icon}</span>
              <span>{active.tag}</span>
              {yearsAgo ? (
                <>
                  <span className="text-paper/30">·</span>
                  <span>{yearsAgo} yrs ago</span>
                </>
              ) : null}
            </div>
            <div
              className="font-headline font-black uppercase leading-[0.9] tracking-tighter text-4xl sm:text-6xl md:text-7xl"
              data-testid="tv-ambient-title"
            >
              {active.label}
            </div>
            <div className="mt-3 font-body italic text-sm sm:text-lg text-paper/80 max-w-2xl">
              {active.hook}
            </div>
            <div className="mt-6 font-mono uppercase tracking-widest text-[10px] text-paper/50">
              {active.date} · {active.region}
            </div>
          </div>
        ) : null}
      </div>

      {/* Big "Watch" pill — always visible, positioned bottom-right */}
      {active?.snapshotId ? (
        <button
          onClick={(e) => { e.stopPropagation(); jumpToWatch(); }}
          className={`absolute bottom-8 right-8 z-20 inline-flex items-center gap-2 bg-press-red text-paper border-2 border-paper/30 px-5 py-3 font-mono uppercase tracking-widest text-xs sm:text-sm hover:bg-paper hover:text-ink transition-all ${
            chromeVisible ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          data-testid="tv-ambient-watch-btn"
          style={{ transition: "opacity 500ms, background-color 200ms, color 200ms" }}
        >
          <Play size={14} /> Watch this day →
        </button>
      ) : null}

      {/* Loading indicator when the very first segment hasn't loaded yet */}
      {!segments[activeIdx]?.loaded ? (
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 text-paper/70 font-mono uppercase tracking-widest text-[10px] z-10 pointer-events-none"
          data-testid="tv-ambient-loading"
        >
          <Loader2 size={14} className="animate-spin" /> Assembling ambient trailer…
        </div>
      ) : null}

      {/* Segment progress dots */}
      <div
        className={`absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-20 transition-opacity duration-500 ${
          chromeVisible ? "opacity-100" : "opacity-0"
        }`}
        data-testid="tv-ambient-progress"
      >
        {segments.map((_, i) => (
          <span
            key={i}
            className="block"
            style={{
              width: i === activeIdx ? 28 : 10,
              height: 3,
              backgroundColor: i === activeIdx ? "#FAF7F2" : "rgba(250,247,242,0.35)",
              transition: "width 400ms, background-color 400ms",
            }}
          />
        ))}
      </div>

      <audio ref={audioRef} preload="auto" />
    </div>
  );
}
